import { useReducer, useRef, useCallback, useEffect } from 'react';
import { getApiClient, isErrorCancel, makeCancelTokenSource } from '../api_client/get';

const WALLET_HISTORY_PAGE_SIZE = 20;

const DISPLAY_KEY_MAP = {
  this_week: 'This Week',
  last_week: 'Last Week',
  earlier: 'Earlier'
};

const SECTION_ORDER = ['Earlier', 'Last Week', 'This Week'];

const reducer = (state, action) => {
  switch (action.type) {
    case 'load-start':
      return { ...state, loading: true, errorMessage: null };
    case 'load-success': {
      const incoming = {};
      Object.entries(action.walletHistory).forEach(([key, data]) => {
        if (!Array.isArray(data) || data.length === 0) return;
        incoming[DISPLAY_KEY_MAP[key] || key] = data;
      });
      const updated = state.sections.map(s =>
        incoming[s.title] ? { ...s, data: [...s.data, ...incoming[s.title]] } : s
      );
      Object.keys(incoming).forEach(title => {
        if (!updated.find(s => s.title === title)) updated.push({ title, data: incoming[title] });
      });
      const sorted = updated.slice().sort(
        (a, b) => SECTION_ORDER.indexOf(a.title) - SECTION_ORDER.indexOf(b.title)
      );
      return { ...state, loading: false, hasFetched: true, sections: sorted, nextCursor: action.nextCursor };
    }
    case 'load-failure':
      return { ...state, loading: false, hasFetched: true, errorMessage: action.message };
    case 'reset':
      return { sections: [], nextCursor: null, loading: false, hasFetched: false, errorMessage: null };
    default:
      return state;
  }
};

const useWalletHistory = () => {
  const [state, dispatch] = useReducer(reducer, {
    sections: [],
    nextCursor: null,
    loading: false,
    hasFetched: false,
    errorMessage: null
  });
  const fetchCtsRef = useRef(null);
  const nextCursorRef = useRef(null);
  const isLoadingRef = useRef(false);

  useEffect(() => {
    return () => { if (fetchCtsRef.current) fetchCtsRef.current.cancel(); };
  }, []);

  const fetchHistory = useCallback(async (cursor) => {
    if (cursor && isLoadingRef.current) return;
    if (fetchCtsRef.current) fetchCtsRef.current.cancel();
    isLoadingRef.current = true;
    const cts = makeCancelTokenSource();
    fetchCtsRef.current = cts;
    if (!cursor) dispatch({ type: 'reset' });
    dispatch({ type: 'load-start' });
    try {
      const response = await getApiClient().getWalletHistory(WALLET_HISTORY_PAGE_SIZE, cursor, cts);
      if (response.data.success) {
        const nextCursor = response.data.next_page_cursor ?? null;
        nextCursorRef.current = nextCursor;
        dispatch({ type: 'load-success', walletHistory: response.data.wallet_history, nextCursor });
      } else {
        dispatch({ type: 'load-failure', message: 'Failed to fetch history.' });
      }
    } catch (error) {
      if (!isErrorCancel(error)) {
        dispatch({ type: 'load-failure', message: 'Something went wrong.' });
      }
    } finally {
      isLoadingRef.current = false;
    }
  }, []);

  const loadMore = useCallback(() => {
    if (nextCursorRef.current && !isLoadingRef.current) {
      fetchHistory(nextCursorRef.current);
    }
  }, [fetchHistory]);

  const cancel = useCallback(() => {
    if (fetchCtsRef.current) fetchCtsRef.current.cancel();
  }, []);

  return {
    sections: state.sections,
    nextCursor: state.nextCursor,
    loading: state.loading,
    hasFetched: state.hasFetched,
    errorMessage: state.errorMessage,
    fetchHistory,
    loadMore,
    cancel
  };
};

export default useWalletHistory;
